// src/index.ts - Main Application Entry Point

// --- Styles ---
import "./styles/main.scss";

// --- Imports ---
// Initialize Sentry as early as possible so any subsequent import-time or
// runtime error can be reported. This replaces the previous CDN loader
// (`https://js.sentry-cdn.com/...`) which was blocked by ad-blockers / Firefox
// ETP and caused a `ReferenceError: Sentry is not defined` on every page.
import { initSentry } from "./utils/sentry";
initSentry();

import { setupGlobalErrorHandlers } from "./utils/errorHandling";
import {
	getElementByIdSafe,
	adjustBodyPaddingForFooter,
	handleAnchorScrollOnLoad,
} from "./utils/domHelpers";
import { initializeSquarePaymentsSDK } from "./components/forms/paymentForm";
import { createAndRenderNav } from "./components/core/navigation";
import { createFooter } from "./components/core/footer";
import { checkLoginState } from "./services/authService";
import { POSTHOG_API_KEY, POSTHOG_API_HOST } from "./utils/config";
import { loadCompetitionCatalogue } from "./services/competitionCatalogue";

// Router imports
import { initializeRouter, handleRouteChange } from "./router";

// Import error renderer for fallback
import { renderErrorMessage } from "./components/common/toastMessage";

// Bootstrap CSS for styling
//import 'bootstrap/dist/css/bootstrap.min.css';

// --- Globals & Third-Party Lib Init ---

function initializePostHog(): void {
	// PostHog is already initialized in the HTML template via the inline snippet
	// Just check if it's available and log status
	if (window.posthog) {
		console.log("PostHog is available");

		// Wait a bit for PostHog to fully load, then check if it's initialized
		setTimeout(() => {
			if (window.posthog?._loaded) {
				console.log("PostHog Initialized and loaded");
			} else {
				console.warn("PostHog exists but may not be fully loaded yet");
			}
		}, 1000);
	} else {
		console.warn("PostHog not available. Survey features will not work.");
	}
}

/** Main application router and initialization function */
async function main(): Promise<void> {
	setupGlobalErrorHandlers();
	void loadCompetitionCatalogue().catch(() => {
		// The repository reports failures; only dependent routes require this data.
	});
	initializePostHog();
	await initializeSquarePaymentsSDK(); // Initialize Square early

	// Check login state early for nav rendering
	await checkLoginState(false);

	// Render common UI
	try {
		// renderNav();
		createAndRenderNav(); // Create and render the navigation bar
		createFooter();
	} catch (error) {
		console.error("Error rendering common UI:", error);
	}

	// Check if content container exists
	const contentContainer = getElementByIdSafe("content");
	if (!contentContainer) {
		console.error("Main content container (#content) not found!");
		document.body.innerHTML = "<h1>Error: Application container not found.</h1>";
		return;
	}

	try {
		// Initialize the client-side router
		// This will handle the initial page load and set up link interception
		await initializeRouter();
	} catch (error: any) {
		console.error("Error initializing router:", error);
		renderErrorMessage(
			"content",
			`Error initializing application: ${error.message || "Unknown error"}`,
		);
	}
}

// --- Event Listeners ---
document.addEventListener("DOMContentLoaded", main);
window.addEventListener("resize", adjustBodyPaddingForFooter);
document.addEventListener("visibilitychange", () => {
	if (document.visibilityState === "visible") {
		void loadCompetitionCatalogue().catch(() => {
			// Keep unrelated pages usable when the CDN is unavailable.
		});
	}
});
// Add listener for popstate (back/forward buttons) if implementing more complex routing/state management
// window.addEventListener('popstate', handleLocationChange);
